import React, {FC, useCallback, useState} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {ChannelModel} from '../../../../../../libs/shared/core/src/models/api/channelModel';
import {PaletteColorsEnum} from '../../../../../../libs/shared/core/src/models/api/paletteModel';
import {BannerContent} from '../../../../../../libs/shared/ui/src/components/banners/bannerContent';
import {Button} from '../../../../../../libs/shared/ui/src/components/buttons/button';
import {InlineLinkButton} from '../../../../../../libs/shared/ui/src/components/buttons/inlineLinkButton';
import {useLinkNavigation} from '../../../../../../libs/shared/ui/src/components/navigation/useLinkNavigation';
import {TooltipCoordinator} from '../../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {VisualSizesEnum} from '../../../../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes, fontWeights} from '../../../../../../libs/shared/ui/src/styles/fontStyles';
import {UserThemeConsumer} from '../../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {PermissionCheck} from '../../../../../../srcCommon/permissions/permissionCheck';
import {buildChannelSettingsPath} from '../../../../../helpers/models/channelHelpers';
import {BasicActivitySplitTargetModel} from '../../../../../models/api/activityModel';
import {PermissionsEnum} from '../../../../../models/api/roleModel';
import {EducationTooltipCoordinator} from '../../../../common/tooltips/educationTooltip/educationTooltipCoordinator';
import {StaticViewerBanner} from '../static/staticViewerBanner';
import {StaticViewerBannerAction} from '../static/staticViewerBannerAction';

/*
 * Style.
 */

const StyledStaticViewerBanner = styled(StaticViewerBanner)`
  font-style: normal;
  margin-top: 17px;
  padding: 0 4px 0 12px;
`;

const StyledBannerContent = styled(BannerContent)`
  display: inline-block;
  font-size: ${fontSizes.verySmall};
  padding: 9px 0 9px 0;
  margin-bottom: 0;
`;

// Needed for BannerIcon to display inline.
const StyledInfoIconWrapperDiv = styled.div`
  display: inline-block;
  margin-bottom: -4px;
  margin-right: 24px;
`;

// Needed for BannerActions to be displayed inline.
const StyledActionButtonWrapperDiv = styled.div`
  display: inline-block;
  position: relative;
  grid-area: action;

  margin-left: 6px;
`;

const StyledStaticViewerBannerAction = styled(StaticViewerBannerAction)`
  &:hover {
    background-color: ${(p) => p.theme.palette.blue.shade30};
  }
`;

const StyledDarkTextSpan = styled.span`
  color: ${(p) => p.theme.palette.grey.shade50};
`;

const StyledActionTextSpan = styled.span`
  color: ${(p) => p.theme.palette.blue.shade50};
  text-transform: uppercase;
`;

const StyledInlineLinkButton = styled(InlineLinkButton)`
  font-weight: ${fontWeights.medium};
`;

/*
 * Props.
 */

export interface SplitConversationBannerProps {
  activity: BasicActivitySplitTargetModel;
  // Activity does not have source teammate if the conversation was split for privacy reasons.
  sourceTeammateName?: string;
  isSplitForPrivacy: boolean;
  isEmailConversation?: boolean;
  isAutoSplit: boolean;
  isLinkConversationSplit: boolean;
  onMergeConversationsClick: (arg: ReadonlyArray<number>) => Promise<void>;
  onSeeOriginalConversationClick: (arg: number) => void;
  onDataLeakLearnMoreClick: () => void;
  firstChannel: ChannelModel | undefined;
}

/*
 * Component.
 */

export const SplitConversationBanner: FC<SplitConversationBannerProps> = (props) => {
  const {
    activity,
    isEmailConversation,
    onMergeConversationsClick,
    onSeeOriginalConversationClick,
    onDataLeakLearnMoreClick,
    firstChannel,
  } = props;

  const {conversationId, originConversationId} = activity;
  const {navigateToLink} = useLinkNavigation();

  const [isMerging, setIsMerging] = useState(false);

  const onMergeClick = async () => {
    // Don't allow the merge button to be clicked twice.
    setIsMerging(true);
    try {
      // TODO: Handle errors.
      await onMergeConversationsClick([conversationId, originConversationId]);
    } finally {
      setIsMerging(false);
    }
  };

  const onViewClick = useCallback(() => {
    onSeeOriginalConversationClick(originConversationId);
  }, [onSeeOriginalConversationClick, originConversationId]);

  // Make sure the activity either has a teammate or was split a reason
  const hasSplitReason = props.isSplitForPrivacy || props.isAutoSplit || props.isLinkConversationSplit;
  if (!props.sourceTeammateName && !hasSplitReason) {
    return null;
  }

  const onAutoSplitSettingsClick = () => {
    if (firstChannel) {
      navigateToLink(buildChannelSettingsPath(firstChannel));
    }
  };

  const onAutoSplitLearnMoreClick = () => {
    navigateToLink('https://help.front.com/en/articles/2307');
  };

  const viewText = isEmailConversation ? (
    <Trans id="activity-banner-split-view-orig">View original</Trans>
  ) : (
    <Trans id="activity-banner-split-view-prev">View previous</Trans>
  );

  const maybeRenderInfoIcon = () => {
    if (props.isSplitForPrivacy) {
      return renderPrivacyInfoIcon(onDataLeakLearnMoreClick);
    }

    if (props.isAutoSplit) {
      const namespaces = firstChannel ? [firstChannel.namespace] : undefined;
      return (
        <PermissionCheck namespaces={namespaces} permission={PermissionsEnum.EDIT_INBOX}>
          {(permissionResult) => (
            <StyledInfoIconWrapperDiv>
              <EducationTooltipCoordinator
                title={<Trans id="activity-banner-split-auto-tooltip-title">Conversation split</Trans>}
                tooltip={
                  <Trans id="activity-banner-split-auto-tooltip-text">
                    Messages have been split into this conversation. You can only reply in the newest
                    conversation with these recipients.
                  </Trans>
                }
                buttons={
                  firstChannel && permissionResult.isAllowed
                    ? [
                        <Button size={VisualSizesEnum.SMALL} onClick={onAutoSplitSettingsClick}>
                          <Trans id="activity-banner-split-auto-tooltip-gotosettings">Go to settings</Trans>
                        </Button>,
                      ]
                    : [
                        <Button size={VisualSizesEnum.SMALL} onClick={onAutoSplitLearnMoreClick}>
                          <Trans id="activity-banner-split-auto-tooltip-learnmore">Learn more</Trans>
                        </Button>,
                      ]
                }
                isInline
              />
            </StyledInfoIconWrapperDiv>
          )}
        </PermissionCheck>
      );
    }

    return null;
  };

  return (
    <StyledStaticViewerBanner
      iconName="splitFilledSmall"
      color={PaletteColorsEnum.BLUE}
      overflowContent={renderReasonText(props)}
    >
      <StyledBannerContent>
        <StyledDarkTextSpan>{renderReasonText(props)}</StyledDarkTextSpan>
      </StyledBannerContent>
      <StyledActionButtonWrapperDiv>
        {maybeRenderInfoIcon()}
        {isEmailConversation && (
          <StyledStaticViewerBannerAction isDisabled={isMerging} onClick={onMergeClick}>
            <StyledActionTextSpan>
              <Trans id="activity-banner-split-merge">Merge</Trans>
            </StyledActionTextSpan>
          </StyledStaticViewerBannerAction>
        )}
        <StyledStaticViewerBannerAction onClick={onViewClick}>
          <StyledActionTextSpan>{viewText}</StyledActionTextSpan>
        </StyledStaticViewerBannerAction>
      </StyledActionButtonWrapperDiv>
    </StyledStaticViewerBanner>
  );
};

/*
 * Helpers.
 */

function renderPrivacyInfoIcon(onDataLeakLearnMoreClick: () => void) {
  return (
    <UserThemeConsumer>
      {(theme) => (
        <StyledInfoIconWrapperDiv>
          <TooltipCoordinator
            isMultiline
            persistent
            placement="top"
            iconColor={theme.palette.grey.shade50}
            render={() => renderPrivacyTooltip(onDataLeakLearnMoreClick)}
          />
        </StyledInfoIconWrapperDiv>
      )}
    </UserThemeConsumer>
  );
}

function renderPrivacyTooltip(onDataLeakLearnMoreClick: () => void) {
  return (
    <>
      <Trans id="activity-banner-split-tooltip-text">Why was a new conversation started?</Trans>{' '}
      <UserThemeConsumer>
        {(theme) => (
          <StyledInlineLinkButton
            color={theme.greys.shade30}
            hoverColor={theme.alphas.white60}
            hasTextDecoration
            onClick={onDataLeakLearnMoreClick}
          >
            <Trans id="activity-banner-duplicates-tooltip-learnmore">Learn more</Trans>
          </StyledInlineLinkButton>
        )}
      </UserThemeConsumer>
    </>
  );
}

function renderReasonText(props: SplitConversationBannerProps) {
  if (props.isSplitForPrivacy) {
    return (
      <Trans id="activity-banner-split-body-private">
        A <strong>new conversation was started</strong> because this message was only sent to you
      </Trans>
    );
  }
  if (props.isAutoSplit) {
    return <Trans id="activity-banner-split-body-auto">Conversation split after inactivity</Trans>;
  }

  if (props.isLinkConversationSplit) {
    return (
      <Trans id="activity-banner-split-body-linkconversation">
        Conversation split due to the creation of this linked conversation
      </Trans>
    );
  }

  if (props.sourceTeammateName) {
    if (props.isEmailConversation) {
      return (
        <Trans id="activity-banner-created-body">Conversation created by {props.sourceTeammateName}</Trans>
      );
    }
    return <Trans id="activity-banner-split-body">Conversation split by {props.sourceTeammateName}</Trans>;
  }

  return null;
}
